import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { shellStatusFilters } from "../filterConfigurations";

const ShellStatus = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Shell Status"]?.edit;

  // Use Firestore hook, order by Status ascending. Collection name "ShellRequestStatus"
  const {
    data: shellStatuses,
    loading,
    error,
  } = useFirestore("ShellRequestStatus", "Status", "asc");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  const pageName = "Shell Status";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(shellStatuses || [], shellStatusFilters);

  const handleRowClick = (params) => {
    if (canEdit) {
      console.log("ShellStatus Main - Row clicked, params.row:", params.row);
      console.log(
        "ShellStatus Main - Active value:",
        params.row.Active,
        "Type:",
        typeof params.row.Active,
      );
      console.log(
        "ShellStatus Main - Approval_Page_Option value:",
        params.row.Approval_Page_Option,
        "Type:",
        typeof params.row.Approval_Page_Option,
      );
      setSelectedStatus(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedStatus(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
    // Data will refresh automatically due to onSnapshot in useFirestore
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    // Data should refresh automatically via useFirestore's onSnapshot.
  };

  // Helper function to get display string for boolean-like values
  const getBooleanDisplayValue = (value) => {
    // For Active field (string-based) - already stored as Yes/No
    if (value === "Yes") {
      return "Yes";
    }
    if (value === "No") {
      return "No";
    }
    // For Approval_Page_Option field (numeric-based)
    if (value === 0 || value === "0") {
      return "No";
    }
    if (value === -1 || value === "-1") {
      return "Yes";
    }
    if (value === null || typeof value === "undefined") {
      return "";
    }
    return String(value);
  };

  const columns = useMemo(
    () => [
      { field: "Status", headerName: "Status", flex: 1, minWidth: 120 },
      { field: "Record_Id", headerName: "Record ID", flex: 1, minWidth: 120 },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "Dropdown_Option",
        headerName: "Dropdown Option",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Approval_Page_Option",
        headerName: "Approval Page Option",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    const processedRows = filteredData.map((status, index) => ({
      id: status.id || index, // Use status.id if available, otherwise use index
      ...status,
    }));

    console.log("ShellStatus Main - Sample row data:", processedRows[0]);
    return processedRows;
  }, [filteredData]);

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching shell status:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={shellStatusFilters}
          onFiltersChange={setFilterModel}
          initialFilters={filterModel}
          collapsible={true}
          title="Shell Status Filters"
          resultCount={filteredData?.length || 0}
          totalCount={shellStatuses?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // Adjust as needed for layout
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap", // Allow wrapping on smaller screens
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="ShellRequestStatus"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={shellStatuses}
              booleanFields={["Approval_Page_Option"]}
              stringBooleanFields={["Active"]}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="ShellRequestStatus" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedStatus && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          statusDetail={selectedStatus} // Pass as statusDetail
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ShellStatus;
